import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

const LIVE_STAGES = ['resolving', 'sending'];
const POLL_MS = 5000;

// Every skipReason the resolver and the worker can write, in plain language. Without this the
// report shows "4 000 skipped" and no explanation at all — and the one reason an admin most
// needs to see, no-unsubscribe-token, is precisely the one a tenant with pre-uuidv4 user
// records would hit in bulk. Raw enum values are never printed at the user.
const SKIP_REASONS = {
    'unsubscribed-user': ['newsletter.skip-unsubscribed-user', 'Unsubscribed (user)'],
    'unsubscribed-customer': ['newsletter.skip-unsubscribed-customer', 'Unsubscribed (customer)'],
    'invalid-email': ['newsletter.skip-invalid-email', 'Invalid email address'],
    duplicate: ['newsletter.skip-duplicate', 'Duplicate — already on this send'],
    'no-unsubscribe-token': ['newsletter.skip-no-unsubscribe-token', 'No unsubscribe link available'],
    cancelled: ['newsletter.skip-cancelled', 'Cancelled before sending'],
};

function reasonText(row) {
    const known = SKIP_REASONS[row.skipReason];
    if (known) {
        return <Text id={known[0]}>{known[1]}</Text>;
    }
    // A failed row carries free-text SES output, not an enum; a skipped row with an
    // unrecognised reason falls back to the raw value so nothing is silently swallowed.
    return row.lastError || row.skipReason || '';
}

// Opens as a share of mails SES accepted. Skipped and failed rows never got the pixel.
function openedPercent(send) {
    const sent = send.sentCount || 0;
    if (sent <= 0) {
        return 0;
    }
    return Math.round(((send.openedCount || 0) / sent) * 100);
}

// Links with at least one click, most clicked first. Returns a NEW array; send.links is
// store state and must not be sorted in place.
function linksWithClicks(send) {
    const links = Array.isArray(send.links) ? send.links : [];
    return links.filter(l => l && l.clickCount > 0).sort((a, b) => b.clickCount - a.clickCount);
}

@observer
class NewsletterSendReport extends Component {
    constructor(props) {
        super(props);
        this.unmounted = false;
    }

    componentDidMount() {
        this.refresh();
    }

    componentWillUnmount() {
        // Both are required. clearTimeout only cancels a timer pending RIGHT NOW; it
        // cannot stop a refresh() already mid-flight from arming a fresh timer when its
        // awaits resolve.
        this.unmounted = true;
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    refresh = async () => {
        const { newsletterStore } = this.props.stores;
        const { sendId } = this.props;
        if (this.unmounted) {
            return;
        }
        const doc = await newsletterStore.loadSend(sendId);
        await newsletterStore.loadSendRecipients(sendId, 'failed');
        // Skipped rows are loaded as their own query rather than by dropping the state
        // filter: an unfiltered page of 100 would be filled with 'sent' rows and the
        // handful of skips would never appear.
        await newsletterStore.loadSendSkipped(sendId);
        await newsletterStore.loadSendOpened(sendId);
        if (this.unmounted) {
            return;
        }
        if (doc && LIVE_STAGES.includes(doc.stage)) {
            this.timer = setTimeout(this.refresh, POLL_MS);
        }
    }

    doCancel = async () => {
        const { newsletterStore } = this.props.stores;
        if (this.timer) {
            clearTimeout(this.timer);
        }
        await newsletterStore.cancelSend(this.props.sendId);
        await this.refresh();
    }

    render() {
        const { newsletterStore } = this.props.stores;
        const {
            send = {}, sendRecipients = [], sendSkipped = [], sendOpened = [],
            sendRecipientsTotal = 0, sendSkippedTotal = 0, sendOpenedTotal = 0,
        } = newsletterStore;
        const topLinks = linksWithClicks(send);
        const live = LIVE_STAGES.includes(send.stage);
        const done = send.totalCount > 0
            ? Math.round((((send.sentCount || 0) + (send.skippedCount || 0) + (send.failedCount || 0)) / send.totalCount) * 100)
            : 0;

        const rows = [
            ...sendRecipients.map(r => ({ ...r, rowState: 'failed' })),
            ...sendSkipped.map(r => ({ ...r, rowState: 'skipped' })),
        ];
        const loadedTotal = sendRecipientsTotal + sendSkippedTotal;
        const capped = loadedTotal > rows.length;

        return (
            <div class='card mt-2'>
                <div class='card-body'>
                    <div class='d-flex justify-content-between align-items-center mb-2'>
                        <span class='badge text-bg-secondary text-uppercase'>{send.stage}</span>
                        {live && (
                            <button type='button' class='btn btn-sm btn-outline-danger' onClick={this.doCancel}>
                                <Text id='newsletter.cancel-send'>Cancel</Text>
                            </button>
                        )}
                    </div>

                    <div class='progress mb-2' style='height: 6px;'>
                        <div class='progress-bar' role='progressbar' style={`width: ${done}%`} />
                    </div>

                    <div class='d-flex flex-wrap gap-3'>
                        <span><Text id='newsletter.sent'>Sent</Text>: {send.sentCount || 0}</span>
                        <span><Text id='newsletter.skipped'>Skipped</Text>: {send.skippedCount || 0}</span>
                        <span><Text id='newsletter.failed'>Failed</Text>: {send.failedCount || 0}</span>
                        <span><Text id='newsletter.total'>Total</Text>: {send.totalCount || 0}</span>
                        <span>
                            <Text id='newsletter.opened'>Opened</Text>: {send.openedCount || 0} ({openedPercent(send)} %)
                        </span>
                        <span><Text id='newsletter.clicked'>Clicked</Text>: {send.clickedCount || 0}</span>
                    </div>
                    <small class='text-body-secondary d-block mt-1'>
                        <Text id='newsletter.opened-caveat'>
                            Opens are recorded through an invisible image. Some mail clients load images automatically and others never do, so the number is an indication.
                        </Text>
                    </small>

                    {rows.length > 0 && (
                        <div class='table-responsive mt-3'>
                            <table class='table table-sm'>
                                <thead>
                                    <tr>
                                        <th><Text id='newsletter.recipient'>Recipient</Text></th>
                                        <th><Text id='newsletter.row-state'>State</Text></th>
                                        <th><Text id='newsletter.reason'>Reason</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.email}</td>
                                            <td>
                                                <span class={`badge ${r.rowState === 'failed' ? 'text-bg-danger' : 'text-bg-secondary'}`}>
                                                    {r.rowState === 'failed'
                                                        ? <Text id='newsletter.failed'>Failed</Text>
                                                        : <Text id='newsletter.skipped'>Skipped</Text>}
                                                </span>
                                            </td>
                                            <td class='text-body-secondary'>{reasonText(r)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {capped && (
                                <small class='text-body-secondary'>
                                    <Text id='newsletter.rows-capped' fields={{ shown: rows.length, total: loadedTotal }}>
                                        Showing {rows.length} of {loadedTotal} skipped and failed recipients.
                                    </Text>
                                </small>
                            )}
                        </div>
                    )}

                    {sendOpened.length > 0 && (
                        <div class='table-responsive mt-3'>
                            <div class='fw-semibold mb-1'><Text id='newsletter.opened'>Opened</Text></div>
                            <table class='table table-sm'>
                                <thead>
                                    <tr>
                                        <th><Text id='newsletter.recipient'>Recipient</Text></th>
                                        <th><Text id='newsletter.first-open'>First open</Text></th>
                                        <th><Text id='newsletter.clicks'>Clicks</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sendOpened.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.email}</td>
                                            <td class='text-body-secondary'>{r.openedDate ? util.isoDate(r.openedDate) : ''}</td>
                                            <td>{r.clickCount || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {sendOpenedTotal > sendOpened.length && (
                                <small class='text-body-secondary'>
                                    <Text id='newsletter.opened-capped' fields={{ shown: sendOpened.length, total: sendOpenedTotal }}>
                                        Showing {sendOpened.length} of {sendOpenedTotal} recipients who opened.
                                    </Text>
                                </small>
                            )}
                        </div>
                    )}

                    {topLinks.length > 0 && (
                        <div class='table-responsive mt-3'>
                            <div class='fw-semibold mb-1'><Text id='newsletter.links'>Links</Text></div>
                            <table class='table table-sm'>
                                <thead>
                                    <tr>
                                        <th><Text id='newsletter.link'>Link</Text></th>
                                        <th><Text id='newsletter.clicks'>Clicks</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topLinks.map(l => (
                                        <tr key={l.index}>
                                            <td class='text-truncate' style='max-width: 420px;' title={l.url}>{l.url}</td>
                                            <td>{l.clickCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default NewsletterSendReport;
